import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Container, IconButton, LinearProgress, Link, Typography } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { DataGrid } from "@mui/x-data-grid";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import moment from "moment";
import currency from "currency.js";
import bkstApi from "../../api";
import BDatePicker from "../../common/input/BDatePicker";
import PmCodeIcon from "../../order/component/PmCodeIcon";
import { pmCodeLabel } from "../../order/component/pmCodeLabel";
import CardBrand from "../../order/component/CardBrand";
import UserAvatar from "../../common/UserAvatar";
import SummaryTile from "../../common/component/SummaryTile";
import ItemFilter from "../../planner/component/ItemFilter";
import { getQueryParam, getStoreFilterStreets, getStoreLocationIdForFilter } from "../../common/util";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { UserContext } from "../../context/UserContext";

const MIN_REPORT_DATE = "2026-06-12";
const MAX_RANGE_DAYS = 31;

const CARD_PM_CODES = new Set(["cp", "cnp", "moto", "cf"]);

function isCardPmCode(pmCode) {
  return CARD_PM_CODES.has(String(pmCode || "").toLowerCase());
}

export default function TransactionReport() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useContext(UserContext);

  const [fromDt, setFromDt] = useState("");
  const [toDt, setToDt] = useState("");
  const [rows, setRows] = useState([]);
  const [posDevices, setPosDevices] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState({});
  const apiRef = useRef(null);
  const storeStreets = getStoreFilterStreets(user);
  const showStoreFilter = storeStreets.length > 1;

  useEffect(() => {
    let [qFrom, qTo] = [
      getQueryParam(location.search, "from"),
      getQueryParam(location.search, "to"),
    ];
    if (!qFrom || !qTo) {
      qTo = moment().format("YYYY-MM-DD");
      qFrom = moment().subtract(1, "day").format("YYYY-MM-DD");
    }
    if (qFrom < MIN_REPORT_DATE) {
      qFrom = MIN_REPORT_DATE;
    }
    if (qTo < qFrom) {
      qTo = qFrom;
    }
    search(qFrom, qTo);
  }, [location.search]);

  useEffect(() => {
    if (toDt < fromDt) {
      setToDt("");
    } else if (fromDt && toDt && moment(toDt).diff(moment(fromDt), "days") > MAX_RANGE_DAYS) {
      setToDt("");
    }
  }, [fromDt, toDt]);

  const runDateSearch = (e) => {
    e.preventDefault();
    if (!fromDt || !toDt || toDt < fromDt || fromDt < MIN_REPORT_DATE) return;
    if (moment(toDt).diff(moment(fromDt), "days") > MAX_RANGE_DAYS) return;
    navigate(`/report/transaction?from=${fromDt}&to=${toDt}`);
  };

  const search = (fromDate, toDate) => {
    setLoading(true);
    setError("");
    setFilter({});
    bkstApi(`/my/report/transaction?fromDt=${fromDate}&toDt=${toDate}`)
      .then((res) => {
        const list = (res.data.transactions || []).map((tx) => ({
          ...tx,
          gridId: `${tx.orderId}_${tx.id}`,
        }));
        setRows(list);
        setPosDevices(
          Object.fromEntries((res.data.posDevices || []).map((device) => [device.uid, device]))
        );
        setFromDt(fromDate);
        setToDt(toDate);
        setLoading(false);
      })
      .catch((e) => {
        setError(e.response?.data?.error || e.message || "Failed to load transactions");
        setLoading(false);
      });
  };

  const getPosLabel = useCallback(
    (dcid) => {
      if (!dcid) return "";
      return posDevices[dcid]?.firstName || dcid;
    },
    [posDevices]
  );

  const filteredRows = useMemo(() => {
    const posValue = filter?.pos?.value;
    const storeId = getStoreLocationIdForFilter(user, filter?.store?.value);
    return rows.filter((row) => {
      if (storeId) {
        // POS → register store; online / no dcid → order fulfillment store
        const locId = row.dcid
          ? posDevices[row.dcid]?.storeLocationId || ""
          : row.storeLocationId || "";
        if (locId !== storeId) {
          return false;
        }
      }
      if (posValue) {
        const normalized = String(posValue).toLowerCase();
        if (getPosLabel(row.dcid).trim().toLowerCase() !== normalized) {
          return false;
        }
      }
      return true;
    });
  }, [rows, filter, getPosLabel, posDevices, user]);

  const posFilterOptions = useMemo(() => {
    const storeId = getStoreLocationIdForFilter(user, filter?.store?.value);
    const dcids = [...new Set(rows.map((tx) => tx.dcid).filter(Boolean))];
    return dcids
      .filter((dcid) => !storeId || posDevices[dcid]?.storeLocationId === storeId)
      .map((dcid) => getPosLabel(dcid))
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b));
  }, [rows, getPosLabel, filter, posDevices, user]);

  const pmCodeBreakdown = useMemo(() => {
    const card = { key: "card", label: "Card", total: 0, count: 0 };
    const cash = { key: "cash", label: "Cash", total: 0, count: 0 };
    const others = {};

    for (const row of filteredRows) {
      const code = String(row.pmCode || "").toLowerCase();
      const amount = Number(row.amount) || 0;

      if (code === "cash") {
        cash.total += amount;
        cash.count++;
      } else if (isCardPmCode(code)) {
        card.total += amount;
        card.count++;
      } else {
        const key = code || "unknown";
        if (!others[key]) {
          others[key] = {
            key,
            label: pmCodeLabel(row.pmCode, user?.config?.paymentMethods),
            total: 0,
            count: 0,
          };
        }
        others[key].total += amount;
        others[key].count++;
      }
    }

    const otherTiles = Object.values(others);
    return [card, cash, ...otherTiles].sort((a, b) => b.total - a.total);
  }, [filteredRows, user?.config?.paymentMethods]);

  const grandTotal = useMemo(() => {
    return filteredRows.reduce((sum, row) => sum + (Number(row.amount) || 0), 0);
  }, [filteredRows]);

  const exportToCSV = () => {
    if (apiRef.current) {
      apiRef.current.exportDataAsCsv({
        fileName: `Transactions_${fromDt}_${toDt}`,
        fields: ["ts", "amount", "pmCode", "card", "dcid", "orderId"],
      });
    }
  };

  const columns = useMemo(
    () => [
      {
        field: "ts",
        headerName: "Timestamp",
        type: "number",
        width: 100,
        valueGetter: (params) => Number(params.row.ts) || 0,
        renderCell: (params) =>
          params.row.ts ? (
            <Box sx={{ py: 0.5, lineHeight: 1.4 }}>
              <Typography variant="body2" display="block">
                {moment(Number(params.row.ts)).format("MMM D, YYYY")}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                {moment(Number(params.row.ts)).format("h:mm A")}
              </Typography>
            </Box>
          ) : (
            "—"
          ),
      },
      {
        field: "amount",
        headerName: "Amount",
        width: 100,
        align: "right",
        headerAlign: "right",
        valueGetter: (params) => Number(params.row.amount) || 0,
        valueFormatter: ({ value }) => currency(value || 0).format(),
        renderCell: (params) => (
          <Typography variant="body2">{currency(params.row.amount || 0).format()}</Typography>
        ),
      },
      {
        field: "pmCode",
        headerName: "Method",
        flex: 1,
        minWidth: 150,
        valueGetter: (params) => {
          const label = pmCodeLabel(params.row.pmCode, user?.config?.paymentMethods);
          return params.row.reference ? `${label} (${params.row.reference})` : label;
        },
        renderCell: (params) => (
          <Box>
            <Box display="flex" alignItems="center" gap={0.75}>
              <PmCodeIcon pmCode={params.row.pmCode} fontSize="small" color="action" />
              <Typography variant="body2">
                {pmCodeLabel(params.row.pmCode, user?.config?.paymentMethods)}
              </Typography>
            </Box>
            {params.row.reference && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ pl: 3.25 }}>
                {params.row.reference}
              </Typography>
            )}
          </Box>
        ),
      },
      {
        field: "card",
        headerName: "Card",
        width: 140,
        sortable: false,
        valueGetter: (params) => {
          const brand = params.row.brand || "";
          const last4 = params.row.last4 || "";
          if (!brand && !last4) return "";
          return last4 ? `${brand} ${last4}`.trim() : brand;
        },
        renderCell: (params) => <CardBrand brand={params.row.brand} last4={params.row.last4} />,
      },
      {
        field: "uid",
        headerName: "Staff",
        width: 72,
        sortable: false,
        renderCell: (params) => (params.row.uid ? <UserAvatar uid={params.row.uid} /> : "—"),
      },
      {
        field: "dcid",
        headerName: "POS",
        width: 72,
        sortable: false,
        valueGetter: (params) => getPosLabel(params.row.dcid),
        renderCell: (params) => (params.row.dcid ? <UserAvatar uid={params.row.dcid} /> : "—"),
      },
      {
        field: "orderId",
        headerName: "Order",
        width: 110,
        renderCell: (params) =>
          params.row.orderId ? (
            <Link component={RouterLink} to={`/order/${params.row.orderId}`} underline="hover">
              View
            </Link>
          ) : (
            "—"
          ),
      },
    ],
    [getPosLabel, user?.config?.paymentMethods]
  );

  if (loading) {
    return <LinearProgress />;
  }

  return (
    <Container maxWidth="md">
      <Box my={4}>
        <Typography variant="h1" gutterBottom>
          Transaction Report
        </Typography>
        <Typography variant="subtitle1" color="textSecondary" gutterBottom>
          Payment ledger transactions for the selected date range.
        </Typography>
        <Box my={3}>
          <form onSubmit={runDateSearch}>
            <Box sx={{ display: "flex", gap: "10px" }}>
              <BDatePicker
                value={fromDt}
                variant="filled"
                label="From Date"
                onChange={(d) => setFromDt(d)}
                required
                autoOk
                hideIcon={true}
                disableFuture
                minDate={moment(MIN_REPORT_DATE).toDate()}
              />
              <BDatePicker
                value={toDt}
                variant="filled"
                label="To Date"
                onChange={(d) => setToDt(d)}
                required
                autoOk
                hideIcon={true}
                disableFuture
                minDate={fromDt ? moment(fromDt).toDate() : moment(MIN_REPORT_DATE).toDate()}
                maxDate={
                  fromDt
                    ? moment.min(moment(), moment(fromDt).add(MAX_RANGE_DAYS, "days")).toDate()
                    : moment().toDate()
                }
              />
              <Button variant="contained" color="primary" type="submit" style={{ height: "56px" }}>
                <SearchIcon />
              </Button>
            </Box>
          </form>
        </Box>
      </Box>

      {error ? (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      ) : null}

      {rows.length > 0 && (
        <>
          <Box my={4}>
            {(posFilterOptions.length > 0 || showStoreFilter) && (
              <Box mb={2}>
                <ItemFilter
                  key={`${fromDt}_${toDt}`}
                  config={[
                    showStoreFilter ? { name: "store", options: storeStreets } : "",
                    posFilterOptions.length > 0
                      ? {
                          name: "pos",
                          options: posFilterOptions,
                        }
                      : "",
                  ]}
                  onChange={(filterUpdate) => {
                    setFilter((current) => ({ ...current, ...filterUpdate }));
                  }}
                />
              </Box>
            )}

            <Box sx={{ mb: 2 }}>
              <SummaryTile
                label="Total"
                amount={currency(grandTotal).format()}
                count={filteredRows.length}
                amountVariant="h3"
                sx={{ flex: "1 1 100%", minWidth: 0 }}
              />
            </Box>

            <Box sx={{ overflowX: "auto" }}>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "nowrap", minWidth: "min-content" }}>
                {pmCodeBreakdown.map((item) => (
                  <SummaryTile
                    key={item.key}
                    label={item.label}
                    amount={currency(item.total).format()}
                    count={item.count}
                  />
                ))}
              </Box>
            </Box>
          </Box>

          {filteredRows.length > 0 ? (
            <Box>
              <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
                <IconButton onClick={exportToCSV}>
                  <FileDownloadOutlinedIcon />
                </IconButton>
              </Box>
              <Box sx={{ height: 620, width: "100%" }}>
                <DataGrid
                  apiRef={apiRef}
                  rows={filteredRows}
                  columns={columns}
                  getRowId={(row) => row.gridId}
                  disableRowSelectionOnClick
                  disableColumnMenu
                  rowHeight={56}
                  initialState={{
                    sorting: { sortModel: [{ field: "ts", sort: "desc" }] },
                  }}
                  sx={{
                    "& .MuiDataGrid-cell": {
                      borderBottom: "1px solid #ECECEC",
                    },
                    "& .MuiDataGrid-columnHeaders": {
                      backgroundColor: "#ECECEC",
                    },
                    "& .MuiDataGrid-row:nth-of-type(odd)": {
                      backgroundColor: "#FAFAFA",
                    },
                    "& .MuiDataGrid-row:nth-of-type(even)": {
                      backgroundColor: "#FFF",
                    },
                    "& .MuiDataGrid-cell:focus": {
                      outline: "none",
                    },
                    "& .MuiDataGrid-cell:focus-within": {
                      outline: "none",
                    },
                  }}
                />
              </Box>
            </Box>
          ) : (
            <Typography color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
              No transactions match the selected POS filter.
            </Typography>
          )}
        </>
      )}

      {!error && rows.length === 0 && (
        <Typography color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
          No transactions found for this date range.
        </Typography>
      )}
    </Container>
  );
}
